/**
 * Single source of truth for auth token access.
 *
 * ALL reads and writes to the underlying storage go through this module —
 * no component, context, or api client should call `localStorage.get/set`
 * directly for auth tokens. That gives us:
 *   1. One place to swap localStorage for HTTP-only cookies later.
 *   2. A safe way to inject a mock in tests.
 *   3. Guaranteed clearing on logout.
 *
 * Storage key names are intentionally namespaced with `gc.` so we don't
 * collide with anything the marketing site or the admin app might write.
 *
 * ── Native (Capacitor) integration ─────────────────────────────────
 * The sync read path returns from the in-memory / localStorage backing
 * store so the axios interceptor stays synchronous. On the native shell
 * we ALSO mirror writes to Capacitor Preferences (a group-scoped,
 * private per-app store — more durable than the WebView's localStorage
 * which the OS can evict aggressively). `hydrateFromNative()` runs once
 * at boot to seed the sync layer from Preferences when localStorage
 * came up empty (e.g. after a WebView cache flush). The web deployment
 * skips both branches — `isNative()` is false and nothing changes.
 */

import { isNative } from '../platform/platform.js';
import * as nativeStorage from '../platform/storage.js';

const ACCESS_KEY  = 'gc.auth.accessToken';
const REFRESH_KEY = 'gc.auth.refreshToken';

// In-memory fallback for environments where window.localStorage is
// unavailable (SSR, disabled cookies, private-mode Safari quotas).
// The module keeps consistent behaviour regardless.
const memory = { [ACCESS_KEY]: null, [REFRESH_KEY]: null };

function backingStore() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      // Probe: some browsers throw on any access when storage is off.
      window.localStorage.getItem(ACCESS_KEY);
      return window.localStorage;
    }
  } catch { /* fall through to in-memory */ }
  return {
    getItem: (k) => (memory[k] ?? null),
    setItem: (k, v) => { memory[k] = String(v); },
    removeItem: (k) => { memory[k] = null; },
  };
}

// Fire-and-forget native mirror. Never awaited on the sync callers so
// the network path can't stall on a preferences write. Best-effort —
// if the plugin isn't available (e.g. in a jsdom test) the promise
// rejects silently.
function mirrorToNative(key, value) {
  if (!isNative()) return;
  if (value == null) nativeStorage.remove(key).catch(() => {});
  else nativeStorage.set(key, value).catch(() => {});
}

export function getAccessToken() {
  return backingStore().getItem(ACCESS_KEY) || null;
}

export function getRefreshToken() {
  return backingStore().getItem(REFRESH_KEY) || null;
}

/**
 * Persist a new token pair. Pass `null` for either token to clear just
 * that one — but the normal shape is "both together" so the two never
 * drift out of sync.
 */
export function setTokens({ accessToken, refreshToken }) {
  const store = backingStore();
  if (accessToken == null) store.removeItem(ACCESS_KEY);
  else store.setItem(ACCESS_KEY, accessToken);
  if (refreshToken == null) store.removeItem(REFRESH_KEY);
  else store.setItem(REFRESH_KEY, refreshToken);
  // Mirror both writes to native storage so a WebView cache flush
  // doesn't sign the user out. Best-effort; the sync store above is
  // the authoritative read path.
  mirrorToNative(ACCESS_KEY, accessToken);
  mirrorToNative(REFRESH_KEY, refreshToken);
}

/** Drop both tokens. Idempotent. */
export function clearTokens() {
  const store = backingStore();
  store.removeItem(ACCESS_KEY);
  store.removeItem(REFRESH_KEY);
  mirrorToNative(ACCESS_KEY, null);
  mirrorToNative(REFRESH_KEY, null);
}

/**
 * One-shot native → sync seed. Called by main.jsx BEFORE the
 * AuthProvider boots so `getAccessToken()` returns the durable token
 * when the WebView localStorage has been evicted. On web this is a
 * no-op and resolves immediately.
 */
export async function hydrateFromNative() {
  if (!isNative()) return;
  try {
    const store = backingStore();
    // Only backfill keys the sync store is missing — never overwrite
    // a fresher sync value with a stale native mirror.
    if (!store.getItem(ACCESS_KEY)) {
      const nativeAccess = await nativeStorage.get(ACCESS_KEY);
      if (nativeAccess) store.setItem(ACCESS_KEY, nativeAccess);
    }
    if (!store.getItem(REFRESH_KEY)) {
      const nativeRefresh = await nativeStorage.get(REFRESH_KEY);
      if (nativeRefresh) store.setItem(REFRESH_KEY, nativeRefresh);
    }
  } catch { /* best-effort */ }
}

// Test-only helper: expose the keys so a test can wipe the store on its
// own reset without importing them separately.
export const _INTERNAL = { ACCESS_KEY, REFRESH_KEY };
